import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';

const ActivationModal = ({
  isOpen,
  onClose,
  activatingUser,
  lineProfile = null,
  onActivationSuccess
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen || !activatingUser) return null;

  const displayName =
    activatingUser.name ||
    `${activatingUser.first_name || ''} ${activatingUser.last_name || ''}`.trim() ||
    activatingUser.email ||
    'เจ้าหน้าที่';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!newPassword || newPassword.length < 6) {
      const msg = 'รหัสผ่านใหม่ต้องมีความยาวอย่างน้อย 6 ตัวอักษร';
      setErrorMsg(msg);
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(msg);
      }
      return;
    }

    if (newPassword !== confirmPassword) {
      const msg = 'รหัสผ่านใหม่ทั้งสองช่องไม่ตรงกัน';
      setErrorMsg(msg);
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(msg);
      }
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      const msg = 'กรุณากรอกอีเมลจริงที่ถูกต้อง';
      setErrorMsg(msg);
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(msg);
      }
      return;
    }

    setLoading(true);
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // 1. Call complete_user_activation RPC
      const { error: rpcError } = await supabase.rpc('complete_user_activation', {
        p_user_id: activatingUser.id,
        p_new_email: cleanEmail,
        p_new_password: newPassword,
        p_line_user_id: lineProfile?.userId || null,
        p_line_display_name: lineProfile?.displayName || null,
        p_line_picture_url: lineProfile?.pictureUrl || null
      });

      if (rpcError) {
        let msg = rpcError.message || 'เกิดข้อผิดพลาดในการเปิดใช้งานบัญชี';
        if (msg.includes('Email already registered') || msg.includes('Email already in use')) {
          msg = `อีเมลนี้ (${cleanEmail}) มีในระบบแล้ว กรุณาใช้อีเมลอื่น เช่น ${cleanEmail.replace('@', '+test@')}`;
        }
        setErrorMsg(msg);
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert(msg);
        }
        setLoading(false);
        return;
      }

      // 2. Sign in with the newly created password
      const { data: loginData } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password: newPassword
      });

      let finalSession = loginData?.session;
      if (!finalSession) {
        finalSession = {
          user: {
            id: activatingUser.id,
            email: cleanEmail,
            name: displayName,
            role: activatingUser.role || 'staff',
            line_user_id: lineProfile?.userId || null,
            avatar_url: lineProfile?.pictureUrl || null
          }
        };
      }

      if (lineProfile?.userId && typeof localStorage !== 'undefined') {
        localStorage.setItem(
          'dental_line_session',
          JSON.stringify({
            user: {
              id: activatingUser.id,
              email: cleanEmail,
              name: displayName,
              role: activatingUser.role || 'staff',
              line_user_id: lineProfile.userId,
              avatar_url: lineProfile.pictureUrl
            }
          })
        );
      }

      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert('🎉 เปิดใช้งานบัญชีสำเร็จ! ยินดีต้อนรับสู่ระบบ Dental CSSD');
      }

      if (onActivationSuccess) {
        onActivationSuccess(finalSession);
      }
      onClose();
    } catch (err) {
      console.error('Activation exception:', err);
      const msg = err.message || 'เกิดข้อผิดพลาด';
      setErrorMsg(msg);
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.7)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          padding: '28px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          maxHeight: '90vh',
          overflowY: 'auto',
          textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.3em', fontWeight: 'bold' }}>
              🏥 เปิดใช้งานบัญชีเจ้าหน้าที่ครั้งแรก
            </h3>
            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.9em' }}>
              ยินดีต้อนรับคุณ <strong style={{ color: '#2563eb' }}>{displayName}</strong>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close-activation-modal"
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              fontSize: '22px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ✕
          </button>
        </div>

        {errorMsg && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              color: '#b91c1c',
              fontSize: '0.88em',
              marginBottom: '16px',
              lineHeight: 1.4
            }}
          >
            ⚠️ {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Step 1: New Password */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.88em', color: '#334155', fontWeight: '600', marginBottom: '6px' }}>
              ขั้นตอนที่ 1: กำหนดรหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="ระบุรหัสผ่านใหม่..."
                required
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.92em',
                  boxSizing: 'border-box'
                }}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="กรอกรหัสผ่านใหม่อีกครั้ง..."
                required
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  borderRadius: '10px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.92em',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  alignSelf: 'flex-start',
                  background: 'none',
                  border: 'none',
                  color: '#475569',
                  fontSize: '0.82em',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {showPassword ? '👁️ ซ่อนรหัสผ่าน' : '👁️‍🗨️ แสดงรหัสผ่าน'}
              </button>
            </div>
          </div>

          {/* Step 2: Email */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '0.88em', color: '#334155', fontWeight: '600', marginBottom: '4px' }}>
              ขั้นตอนที่ 2: ระบุอีเมลส่วนตัวหรืออีเมลที่ใช้งานจริง
            </label>
            <p style={{ margin: '0 0 8px 0', fontSize: '0.8em', color: '#64748b' }}>
              สำหรับยืนยันตัวตนและกู้คืนรหัสผ่าน (ต้องเป็นอีเมลจริงที่ไม่เคยลงทะเบียนในระบบ)
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="เช่น yourname@hospital.com หรือ email@gmail.com"
              required
              style={{
                width: '100%',
                padding: '11px 14px',
                borderRadius: '10px',
                border: '1.5px solid #cbd5e1',
                fontSize: '0.92em',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Optional LINE Connection Card if profile available */}
          {lineProfile?.displayName && (
            <div
              style={{
                marginBottom: '20px',
                padding: '12px 14px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              {lineProfile.pictureUrl ? (
                <img
                  src={lineProfile.pictureUrl}
                  alt="LINE Profile"
                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#06C755', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>
                  L
                </div>
              )}
              <div>
                <div style={{ fontSize: '0.82em', color: '#166534', fontWeight: '600' }}>
                  🟢 ผูกกับ LINE: {lineProfile.displayName}
                </div>
                <div style={{ fontSize: '0.75em', color: '#475569' }}>
                  บัญชีนี้จะถูกผูกกับ LINE ID ของคุณโดยอัตโนมัติ
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 20px',
                backgroundColor: '#f1f5f9',
                color: '#475569',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.95em',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#94a3b8' : '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.98em',
                fontWeight: 'bold',
                cursor: loading ? 'wait' : 'pointer',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? 'กำลังเปิดใช้งาน...' : 'ยืนยันและเปิดใช้งานบัญชี'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivationModal;
